/**
 * Module-level source of truth for the cockpit theme.
 *
 * Lives OUTSIDE React on purpose: the logout teardown and the post-login
 * hydration both have to move the DOM class and the React state in one step,
 * and neither of them runs inside a component. `ThemeProvider` is a thin
 * `useSyncExternalStore` shell over this file.
 *
 * Persistence stays on `veltric.theme`, which the pre-paint script in
 * index.html reads before the bundle boots. Product default is light; only an
 * explicit "dark" flips.
 */

import { getPref, removePref, setPref, subscribePref } from "@/lib/storage/userPrefs";

export type Theme = "light" | "dark";

export const THEME_KEY = "veltric.theme";
export const DEFAULT_THEME: Theme = "light";

export function normalizeTheme(value: string | null | undefined): Theme {
  return value === "dark" ? "dark" : "light";
}

function applyToDom(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

let current: Theme = normalizeTheme(getPref(THEME_KEY));
const listeners = new Set<() => void>();

// The pre-paint script in index.html has normally already done this; re-assert
// it once at boot so a tab whose DOM and stored value ever drift (private mode
// throw, e2e seeding) starts from ONE truth.
applyToDom(current);

function emit(): void {
  // Copy first: a listener may unsubscribe during the walk.
  for (const listener of [...listeners]) listener();
}

export function getTheme(): Theme {
  return current;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Applies `next` to the DOM and (unless `persist: false`) to localStorage.
 *
 * `persist: false` is for the cross-tab echo: a tab that only LEARNED about a
 * change must not write it back, or two tabs holding different accounts push
 * each other back and forth forever (the reported "flackern").
 */
export function setTheme(next: Theme, opts?: { persist?: boolean }): void {
  applyToDom(next);
  if (opts?.persist !== false) setPref(THEME_KEY, next);
  if (current === next) return;
  current = next;
  emit();
}

/**
 * Logout: drop the account's cached choice AND paint the default, so the DOM,
 * localStorage and the pre-paint script agree from this moment on. Removing
 * the key without this used to leave the login screen in the previous
 * account's colours until the next hard reload.
 */
export function resetThemeToDefault(): void {
  applyToDom(DEFAULT_THEME);
  removePref(THEME_KEY);
  if (current === DEFAULT_THEME) return;
  current = DEFAULT_THEME;
  emit();
}

// Cross-tab: adopt what another tab stored, never write it back.
subscribePref(THEME_KEY, (value) => {
  setTheme(normalizeTheme(value), { persist: false });
});
